import Service from 'react-reservice';

import { IPriceAlert, IPriceAlertConfig } from '@aqx-core/common/api/price-alert';
import {
  EPriceAlertTab,
  MAX_ALERTS_FOR_ALL_SYMBOLS,
  MAX_ALERTS_PER_SYMBOL
} from '@aqx-core/common/constants/price-alert';

import { ILivePricesMap, IPriceAlertSymbolDetailMap, ISymbolDisabledMap, ISymbolPriceAlertsMap } from './types';

interface IPriceAlertServiceContext {
  currentSymbol?: string;

  priceAlertsMap: ISymbolPriceAlertsMap;
  livePricesMap: ILivePricesMap;
  symbolDetailsMap: IPriceAlertSymbolDetailMap;
  symbolDisabledMap: ISymbolDisabledMap;

  priceAlertsLoading: boolean;
  alertConfigLoading: boolean;
  submitting: boolean;

  showBulkEdit: boolean;
  selectedAlerts: string[];

  activeTab: EPriceAlertTab;

  editAlertId?: string;

  alertConfig: IPriceAlertConfig;
}

const defaultContext: IPriceAlertServiceContext = {
  currentSymbol: undefined,

  priceAlertsMap: {},
  livePricesMap: {},
  symbolDetailsMap: {},
  symbolDisabledMap: {},

  priceAlertsLoading: false,
  alertConfigLoading: false,
  submitting: false,

  showBulkEdit: false,
  selectedAlerts: [],

  activeTab: EPriceAlertTab.CURRENT_SYMBOL,

  alertConfig: {
    symbolLimit: MAX_ALERTS_PER_SYMBOL,
    accountLimit: MAX_ALERTS_FOR_ALL_SYMBOLS
  }
};

class PriceAlertService extends Service<IPriceAlertServiceContext> {
  constructor() {
    super();

    this.context = defaultContext;
  }

  clearCurrentSymbol = () => {
    this.context.currentSymbol = undefined;
  };

  setCurrentSymbol = (symbol: string) => {
    this.context.currentSymbol = symbol;
  };

  clearPriceAlertsMap = () => {
    this.context.priceAlertsMap = {};
  };

  setPriceAlertsMap = (priceAlertsMap: ISymbolPriceAlertsMap) => {
    this.context.priceAlertsMap = priceAlertsMap;
  };

  updatePriceAlert = (newPriceAlert: IPriceAlert) => {
    const { symbol } = newPriceAlert;
    if (!this.context.priceAlertsMap[symbol]) {
      return;
    }

    const existingAlert = this.context.priceAlertsMap[symbol].alerts[newPriceAlert.alertId];

    const newSymbolPriceAlertObj = {
      ...this.context.priceAlertsMap[symbol],
      alerts: {
        ...this.context.priceAlertsMap[symbol].alerts,
        [newPriceAlert.alertId]: {
          ...existingAlert,
          ...newPriceAlert
        }
      }
    };
    this.context.priceAlertsMap = {
      ...this.context.priceAlertsMap,
      [symbol]: newSymbolPriceAlertObj
    };
  };

  clearLivePricesMap = () => {
    this.context.livePricesMap = {};
  };

  updateLivePricesMap = (incomingLivePricesMap: ILivePricesMap) => {
    this.context.livePricesMap = {
      ...this.context.livePricesMap,
      ...incomingLivePricesMap
    };
  };

  clearSymbolDisabledMap = () => {
    this.context.symbolDisabledMap = {};
  };

  updateSymbolDisabledMap = (incomingSymbolDisabledMap: ISymbolDisabledMap) => {
    this.context.symbolDisabledMap = {
      ...this.context.symbolDisabledMap,
      ...incomingSymbolDisabledMap
    };
  };

  updateBidPriceOnLivePrices = (bid: number, symbol: string) => {
    if (!this.context.livePricesMap[symbol]) {
      return;
    }

    this.context.livePricesMap = {
      ...this.context.livePricesMap,

      [symbol]: {
        ...this.context.livePricesMap[symbol],
        bid
      }
    };
  };

  clearSymbolDetailsMap = () => {
    this.context.symbolDetailsMap = {};
  };

  updateSymbolDetailsMap = (incomingSymbolDetailsMap: IPriceAlertSymbolDetailMap) => {
    this.context.symbolDetailsMap = {
      ...this.context.symbolDetailsMap,
      ...incomingSymbolDetailsMap
    };
  };

  setPriceAlertsLoading = (loading: boolean) => {
    this.context.priceAlertsLoading = loading;
  };

  setAlertConfigLoading = (loading: boolean) => {
    this.context.alertConfigLoading = loading;
  };

  setSubmitting = (submitting: boolean) => {
    this.context.submitting = submitting;
  };

  setShowBulkEdit = (showBulkEdit: boolean) => {
    this.context.showBulkEdit = showBulkEdit;
  };

  addAlertIdsToSelected = (ids: string[]) => {
    const newSelectedAlerts = new Set([...this.context.selectedAlerts]);
    ids.forEach((id: string) => {
      if (!newSelectedAlerts.has(id)) {
        newSelectedAlerts.add(id);
      }
    });
    this.context.selectedAlerts = Array.from(newSelectedAlerts);
  };

  removeAlertIdsFromSelected = (ids: string[]) => {
    const newSelectedAlerts = new Set([...this.context.selectedAlerts]);
    ids.forEach((id: string) => {
      if (newSelectedAlerts.has(id)) {
        newSelectedAlerts.delete(id);
      }
    });
    this.context.selectedAlerts = Array.from(newSelectedAlerts);
  };

  clearAllSelectedAlerts = () => {
    this.context.selectedAlerts = [];
  };

  setAllSelectedAlerts = (alertIds: string[]) => {
    this.context.selectedAlerts = alertIds;
  };

  clearEditAlertId = () => {
    this.context.editAlertId = undefined;
  };

  setEditAlertId = (alertId: string) => {
    this.context.editAlertId = alertId;
  };

  removePriceAlerts = (alertIds: string[]) => {
    const newMap = { ...this.context.priceAlertsMap };

    for (const symbol of Object.keys(newMap)) {
      const symbolAlerts = newMap[symbol];
      const newAlerts = { ...symbolAlerts.alerts };
      let changed = false;

      for (const id of alertIds) {
        if (newAlerts[id]) {
          delete newAlerts[id];
          changed = true;
        }
      }

      if (changed) {
        if (Object.keys(newAlerts).length === 0) {
          delete newMap[symbol];
        } else {
          newMap[symbol] = { ...symbolAlerts, alerts: newAlerts };
        }
      }
    }

    this.context.priceAlertsMap = newMap;
  };

  setActiveTab = (tab: EPriceAlertTab) => {
    this.context.activeTab = tab;
  };

  setAlertConfig = (config: IPriceAlertConfig) => {
    this.context.alertConfig = config;
  };

  // Use for unit testing only
  resetContext = () => {
    this.context = { ...defaultContext };
  };
}

export const priceAlertService = new PriceAlertService();
